import { useState } from "react";
import TableTemplate, {
  IAdditional,
  IAdditionalHeaderProps,
} from "../../TableTemplate/tableTemplate";
import { TableCell } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  deleteDeltaHedgeOpenOrders,
  deleteDeltaHedgeOrders,
  deltaHedgeOrderTablePause,
  deltaHedgeOrderTablePlay,
  getDeltaHedgeOrderBrokerOrderTable,
  getDeltaHedgeOrderChancesTable,
  getDeltaHedgeOrderChart,
  getDeltaHedgeOrderEvents,
} from "../../../Requests/deltaHedgeOrder";
import { toast } from "react-toastify";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";
import TimelineIcon from "@mui/icons-material/Timeline";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseIcon from "@mui/icons-material/Pause";
import { IDeltaHedgeOrderTableReport } from "../../../Interfaces/deltaHedgeOrder";
import { useAppDispatch } from "../../../App/hooks";
import {
  setDeltaHedgeEditFlag,
  setDeltaHedgeEditRowValues,
} from "../../../Features/DeltaHedgeOrder/deltaHedgeOrderSlice";
import useRequest from "../../../Hooks/useRequest";
import { WrappedTimeSeriesChart } from "../../../Components/Timeseries/WrappedTimeSeriesChart";
import { CommonTableUI } from "../../TableTemplate/common";
import Modal from "../../../Components/Modal/Modal";
import HuntStrategyChancesTable from "../../Order/HuntChances/HuntStrategyChancesTable";
import Button from "../../../Components/Button";
import { OrderStatus } from "../../../Interfaces/orderInterfaces";
import ShekarMetricEventsModalContent from "../../ShekarMetricEvents/OrderMetricEventsModal";

const DeltaHedgeOrderTableAdditionalHeader = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onAction,
  isSelectAll,
}: IAdditionalHeaderProps) => {
  const [loading, setLoading] = useState(false);

  const bulkDeleteOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    deleteDeltaHedgeOrders(selectedRowsIds)
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
        setSelectedRowsIds([]);
      })
      .catch(packetErrorHandler);
  };

  const bulkPauseOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    deltaHedgeOrderTablePause(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const bulkPlayOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    deltaHedgeOrderTablePlay(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const cancelAllOpenOrdersHandler = () => {
    if (!selectedRowsIds.length) return;
    setLoading(true);
    deleteDeltaHedgeOpenOrders(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      <TableCell></TableCell>
      <TableCell>
        <div className="flex flex-row">
          <div
            onClick={selectedRowsIds.length ? bulkPauseOrderHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PauseIcon
              className={`text-sky-900 cursor-pointer  ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
          <div
            onClick={selectedRowsIds.length ? bulkPlayOrderHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PlayCircleIcon
              className={`text-sky-900 cursor-pointer ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
        </div>
      </TableCell>
      <TableCell></TableCell>

      <TableCell>
        <div
          onClick={selectedRowsIds.length ? cancelAllOpenOrdersHandler : undefined}
          className={`text-xs py-1 px-2 rounded ${
            selectedRowsIds.length
              ? "bg-red-700 text-white cursor-pointer"
              : "bg-white text-gray-500 border-[1px] border-gray-300 cursor-not-allowed"
          }`}
        >
          حذف سفارشات باز
        </div>
      </TableCell>
      <TableCell></TableCell>

      <TableCell></TableCell>

      <TableCell>
        <div
          className="hover:bg-red-100 transition-all rounded p-1"
          onClick={selectedRowsIds.length ? bulkDeleteOrderHandler : undefined}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
    </>
  );
};

const DeltaHedgeOrderTableAdditionalCol: React.FC<{
  row: IDeltaHedgeOrderTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [isChancesOpen, setIsChancesOpen] = useState(false);
  const [isBrokerOrderTableOpen, setIsBrokerOrderTableOpen] = useState(false);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const editHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setDeltaHedgeEditRowValues({ ...row }));
    dispatch(setDeltaHedgeEditFlag(true));
  };

  const orderGraphRequest = useRequest(
    () => getDeltaHedgeOrderChart(row.id),
    packetErrorHandler
  );

  const cancelOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    deleteDeltaHedgeOrders([row.id])
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const pauseOrderHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    deltaHedgeOrderTablePause([row.id])
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const playOrderHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    deltaHedgeOrderTablePlay([row.id])
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const getGraphModalUI = () => {
    if (!orderGraphRequest.response) return null;
    return (
      <div className="chart-fill-modal">
        <WrappedTimeSeriesChart dataset={orderGraphRequest.response} className="h-full w-full" />
      </div>
    );
  };

  const DeltaHedgeModalBtnClickHandler = () => {
    orderGraphRequest.fetchData();
    setIsGraphModalOpen(true);
  };

  const eventsModalBtnClickHandler = () => {
    if (!row.algorithm_order_id || !row.condition_id) {
      toast.error("algorithm_order_id و condition_id برای این ردیف موجود نیست");
      return;
    }

    setIsEventsModalOpen(true);
  };

  const cancelOpenOrdersHandler = () => {
    setLoading(true);
    deleteDeltaHedgeOpenOrders([row.id])
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getBrokerOrderTableUI = () => (
    <TableTemplate<{ id: number }, any>
      refreshTableTime={5000}
      getDataTable={(_, offset, pageSize) =>
        getDeltaHedgeOrderBrokerOrderTable({ id: row.id }, offset, pageSize)
      }
      TableUI={({ reports, onAction }) => (
        <CommonTableUI reports={reports} onAction={onAction} />
      )}
    />
  );

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      <TableCell>
        <div
          className="hover:bg-slate-300 transition-all rounded p-1"
          onClick={editHandler}
        >
          <EditIcon className="text-sky-700 cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        {row.isActive ? (
          <div className="hover:bg-slate-300 transition-all rounded p-1">
            <PauseIcon
              className="text-sky-700 cursor-pointer"
              onClick={pauseOrderHandler}
            />
          </div>
        ) : null}
        {row.isActive === false ? (
          <div className="hover:bg-slate-300 transition-all rounded p-1">
            <PlayCircleIcon
              className="text-sky-700 cursor-pointer"
              onClick={playOrderHandler}
            />
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        {isChancesOpen ? (
          <Modal title="شانس‌ها"
            UI={
              <HuntStrategyChancesTable
                getData={(_, offset, pageSize) =>
                  getDeltaHedgeOrderChancesTable(
                    { id: row.id },
                    offset,
                    pageSize
                  )
                }
              />
            }
            onClose={() => setIsChancesOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsChancesOpen(true)}
          text="شانس ها"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isBrokerOrderTableOpen ? (
          <Modal title="سفارشات"
            UI={getBrokerOrderTableUI()}
            onClose={() => setIsBrokerOrderTableOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsBrokerOrderTableOpen(true)}
          text="سفارشات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        <>
          {isGraphModalOpen ? (
            <Modal title="نمودار"
              onClose={() => setIsGraphModalOpen(false)}
              UI={getGraphModalUI()}
              fullScreen
            />
          ) : null}
          <Button
            onClick={DeltaHedgeModalBtnClickHandler}
            text={<TimelineIcon className="!text-base" />}
            variant="contained"
            className="table-btn"
          />
        </>
      </TableCell>
      <TableCell>
        {isEventsModalOpen ? (
          <Modal title="رویدادهای شکار"
            onClose={() => setIsEventsModalOpen(false)}
            UI={
              <ShekarMetricEventsModalContent
                algorithmOrderId={row.algorithm_order_id}
                conditionId={row.condition_id}
                getEvents={getDeltaHedgeOrderEvents}
              />
            }
          />
        ) : null}
        <Button
          onClick={eventsModalBtnClickHandler}
          text="events"
          variant="contained"
          className="table-btn"
          disabled={!row.algorithm_order_id || !row.condition_id}
        />
      </TableCell>
      <TableCell>
        {row.orderStatus !== OrderStatus.Cancelled ? (
          <div
            onClick={cancelOrderHandler}
            className="hover:bg-red-100 transition-all rounded p-1"
          >
            <DeleteIcon
              fontSize="small"
              className="text-red-700 cursor-pointer"
            />
          </div>
        ) : null}
      </TableCell>
    </>
  );
};

export const DeltaHedgeOrderTableAdditional: IAdditional<IDeltaHedgeOrderTableReport> =
  {
    getHeader: DeltaHedgeOrderTableAdditionalHeader,
    getRow: DeltaHedgeOrderTableAdditionalCol,
  };
